import React, { useState } from 'react'

import { Form, Formik } from 'formik'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'

import { KickBackFormikContext } from './KickBackFormikContext'
import { setIsKickBackModalOpen } from './kickBackSlice'
import { useAppSelector } from '@/client/hooks'
import { Drawer } from '@/components/Drawer/DrawerV2'
import { InputField } from '@/components/FormikInputs'
import { graphqlApi } from '@/graphql'
import { useUpdateSubStageMutation } from '@/services/application'

const reasons = [
  {
    value: 'missing-data',
    label: 'Missing Data',
    description: 'Essential data components or elements are missing from the file.',
  },
  {
    value: 'data-mismatch',
    label: 'Data Mismatch',
    description: 'The provided data doesn’t correspond with expected or referenced values.',
  },
  {
    value: 'missing-documentation',
    label: 'Incomplete/Missing Documentation',
    description:
      'The file is missing key documents or has incomplete paperwork that supports its main content.',
  },
  {
    value: 'expired-documentation',
    label: 'Expired Documentation',
    description:
      'The file contains records or documentation that are outdated and need renewal or updating.',
  },
  {
    value: 'suspicious-data',
    label: 'Suspicious Data',
    description:
      'The data in the file raises questions or appears doubtful, necessitating further scrutiny.',
  },
  {
    value: 'investor-guidelines-breach',
    label: 'Investor Guidelines Breach',
    description:
      'The file does not adhere to established guidelines or standards set by investors, suggesting potential risks and serious non-compliance.',
  },
]

const qualityCodes = [
  { value: 'l1', label: 'L1 (Minor Issue)' },
  { value: 'l2', label: 'L2 (Major Issue)' },
]

export const KickBackModal = () => {
  const dispatch = useDispatch()

  const [updateSubStage, { isLoading: isUpdating }] = useUpdateSubStageMutation()

  const id = useAppSelector((state) => state.kickback.kickBackApplication)
  const isKickBackModalOpen = useAppSelector((state) => state.kickback.isKickBackModalOpen)
  const updateParentCellValue = useAppSelector(
    (state) => state.kickback.kickBackModalClosedCallback
  )
  const stages = useAppSelector((state) => state.kickback.stages)

  const [step, setStep] = useState<1 | 2 | 3>(1)

  const initialValues = {
    reason: '',
    reason_description: '',
    quality_code: '',
  }

  const bypassKickBackWorkFlow = async () => {
    if (typeof id === 'string') {
      const response = await updateSubStage({
        external_id: id,
        application_state: stages.to.value,
      })
      shouldUpdateWQ(response)

      dispatch(setIsKickBackModalOpen(false))
    }
  }

  const shouldUpdateWQ = (response) => {
    // Check if we should update WorkQueues cell value
    if ('data' in response && updateParentCellValue) {
      updateParentCellValue?.(response?.data?.application_state)
    }
  }

  return (
    <Drawer
      title='Workflow / Deal Kick-back'
      isOpen={isKickBackModalOpen}
      setIsOpen={(isOpen) => dispatch(setIsKickBackModalOpen(isOpen))}
    >
      <div className='bg-white h-[91.5vh]'>
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object().shape({
            reason: Yup.string().required('Reason is required!'),
            quality_code: Yup.string().required('Quality Code is required!'),
          })}
          onSubmit={async (values) => {
            if (typeof id === 'string') {
              const response = await updateSubStage({
                external_id: id,
                application_state: stages.to.value,
                kick_back_data: {
                  ...values,
                  previous_substage: stages.from.value,
                  to_substage: stages.to.value,
                },
              })
              shouldUpdateWQ(response)
              dispatch(setIsKickBackModalOpen(false))
              dispatch(graphqlApi.util.invalidateTags(['gql:application']))
            }
          }}
          isInitialValid={false}
        >
          {({ values, setFieldValue, isValid }) => (
            <Form className='flex flex-col justify-between px-4 pb-4 py-2 h-full'>
              <div>
                <div
                  className={`collapse border-l-2 rounded-none overflow-visible ${
                    step === 1 ?? 'collapse-open'
                  }`}
                >
                  <input
                    type='checkbox'
                    className='w-full min-h-10'
                    checked={step === 1}
                    onClick={() => setStep(1)}
                  />
                  <div className='absolute -left-[1.1rem] flex gap-2 collapse-title min-h-10	p-0 text-gray-400'>
                    <div className='flex items-center justify-center w-8 h-8 bg-accent rounded-full text-white text-center'>
                      <p>1</p>
                    </div>{' '}
                    Please select the kick-back reason type(s)
                  </div>
                  <div className='collapse-content ml-2'>
                    {reasons.map((reason) => (
                      <div
                        key={reason.value}
                        className='my-4 flex gap-2'
                      >
                        <button
                          className='cursor-pointer rounded-full border border-gray-200 h-fit my-1.5'
                          onClick={() => setFieldValue('reason', reason.value)}
                          type='button'
                        >
                          <div
                            className={`rounded-full w-3 h-3 m-px ${
                              values.reason === reason.value ? 'bg-sky-400' : ''
                            }`}
                          ></div>
                        </button>
                        <p className='text-gray-400'>
                          {reason.label}: {reason.description}
                        </p>
                      </div>
                    ))}
                    <div className='w-full flex justify-end my-2'>
                      <button
                        type='button'
                        onClick={() => setStep(2)}
                        disabled={!values?.reason}
                        className='btn btn-primary'
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
                <div className='collapse border-l-2 overflow-visible rounded-none'>
                  <input
                    type='checkbox'
                    className='w-full min-h-10'
                    checked={step === 2}
                    onClick={() => setStep(2)}
                  />
                  <div className='absolute -left-[1.1rem] flex gap-2 collapse-title min-h-10 p-0 text-gray-400'>
                    <div className='flex items-center justify-center w-8 h-8 bg-accent rounded-full text-white text-center'>
                      <p>2</p>
                    </div>{' '}
                    Describe the reason - optional
                  </div>
                  <div className='collapse-content pr-9'>
                    <InputField
                      placeholder='Kick-back Reason / Text-area'
                      name='reason_description'
                      as='textarea'
                      style='default'
                    />
                    <div className='w-full flex justify-end my-2'>
                      <button
                        type='button'
                        onClick={() => setStep(3)}
                        className='btn btn-primary'
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
                <div className='collapse overflow-visible rounded-none'>
                  <input
                    type='checkbox'
                    className='w-full min-h-10'
                    checked={step === 3}
                    onClick={() => setStep(3)}
                  />
                  <div className='absolute -left-4 flex gap-2 collapse-title min-h-10 p-0 text-gray-400'>
                    <div className='flex items-center justify-center w-8 h-8 bg-accent rounded-full text-white text-center'>
                      <p>3</p>
                    </div>{' '}
                    Select a quality code
                  </div>
                  <div className='collapse-content ml-2 gap-6'>
                    {qualityCodes.map((code) => (
                      <div key={code.value}>
                        <div className='my-4 flex gap-2 items-center'>
                          <button
                            className='cursor-pointer rounded-full border border-gray-200 p-px'
                            onClick={() => setFieldValue('quality_code', code.value)}
                            type='button'
                          >
                            <div
                              className={`rounded-full w-3 h-3 ${
                                values.quality_code === code.value ? 'bg-sky-400' : ''
                              }`}
                            ></div>
                          </button>
                          <p className='text-gray-400'>{code.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className='flex ml-auto self-end gap-4'>
                <button
                  type='button'
                  onClick={bypassKickBackWorkFlow}
                  className='btn btn-primary'
                >
                  Bypass
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={!isValid || isUpdating || step !== 3}
                >
                  Finish
                </button>
              </div>
              <KickBackFormikContext />
            </Form>
          )}
        </Formik>
      </div>
    </Drawer>
  )
}
